import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2, Upload, Trash2, Save, RefreshCw, Lock, Globe, ExternalLink, Printer, Camera, Image,
  Share2, ShieldCheck, AlertTriangle, XCircle, CheckCircle2, Palette, ChevronDown, Eye, History,
  FileText, Download, RotateCcw, HelpCircle, ScrollText, FileCheck, X,
} from 'lucide-react';
import { api, fileUrl, API } from '../../lib/api';
import { toast } from 'sonner';

const SETTINGS_FIELDS = [
  ['title', 'Title'],
  ['subtitle', 'Subtitle'],
  ['slogan', 'Slogan'],
  ['footer', 'Footer line'],
  ['disclaimer', 'Disclaimer'],
  ['url', 'Link URL (site path or https://)'],
  ['qr_destination', 'QR destination (site path or https://)'],
  ['colors', 'Colors (e.g. navy #1a2744 / gold #C9A84C)'],
  ['placement', 'Placement (where this design is used)'],
];

const fmtSize = (b) => (!b ? '—' : b > 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const VBadge = ({ cls, icon: Icon, children, testId }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${cls}`} data-testid={testId}>
    {Icon && <Icon className="h-3 w-3" />} {children}
  </span>
);

// ---- Part 1: Flyer Upload Walkthrough (dismissible first-run help card, admin-only) ----
const WALKTHROUGH_KEY = 'w8_design_walkthrough_dismissed';
const WALKTHROUGH_STEPS = [
  'Upload the replacement file (it is saved Private / Not Public).',
  'Preview the file with the Preview / Download button in Version history.',
  'Run the safety scan — it opens automatically in the Activate dialog.',
  'Check the confirmation box (no restricted CKL imagery, Google Maps screenshots, placeholders, retired slogans, or unsupported claims).',
  'Mark Approved — activating as Public approves the version automatically.',
  'Set Active — this version becomes the selected one for its design.',
  'Set Public if it should appear on the site or replace the public download.',
  'Test the public download to confirm the new file is served.',
  'Confirm analytics still track in Launch Content Setup → Download Analytics.',
];

const SafetyRulesDialog = ({ open, onClose, confirmationText }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="design-safety-rules-dialog">
      <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Design safety rules</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm text-slate-600">
        <p><strong className="text-[#1a2744]">Every new upload is Private / Not Public by default.</strong> Private uploads are never blocked — the rules below only gate public use.</p>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-bold text-red-800 text-xs uppercase tracking-wide mb-1.5">Blockers (stop public activation)</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-red-800">
            <li>File missing from storage</li>
            <li>Retired wording in the file or fields (Action Hub, Taxpayer Action Hub, old slogans, 5% tax target)</li>
            <li>Google Maps references inside the file</li>
            <li>Unsafe QR destination or URL (javascript:, data:, and similar schemes)</li>
            <li>Wrong file type for a public download slot (flyer PDF/DOCX)</li>
          </ul>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="font-bold text-amber-800 text-xs uppercase tracking-wide mb-1.5">Warnings (review before continuing)</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-amber-800">
            <li>Filename suggests placeholder artwork or restricted imagery (Google/Maps/screenshot/CKL terms)</li>
            <li>Possible placeholder text inside the file</li>
            <li>Image contents cannot be text-scanned automatically</li>
          </ul>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="font-bold text-[#1a2744] text-xs uppercase tracking-wide mb-1.5">Required confirmation before public activation</p>
          <p className="text-xs italic">“{confirmationText || 'I confirm this design contains no restricted CKL imagery, no Google Maps screenshots, no placeholder artwork, no retired slogans, and no unsupported claims.'}”</p>
        </div>
        <Button onClick={onClose} variant="outline" className="w-full h-10 font-bold btn-press" data-testid="design-safety-rules-close">Close</Button>
      </div>
    </DialogContent>
  </Dialog>
);

const WalkthroughCard = ({ onDismiss, confirmationText }) => {
  const [rulesOpen, setRulesOpen] = useState(false);
  const scrollToUpload = () => {
    document.querySelector('[data-testid="design-card-campaign-flyer-pdf"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.info('Use “Upload version (saved Private)” on the design you want to replace.');
  };
  return (
    <Card className="mb-4 border-[#C9A84C]/50 bg-[#C9A84C]/5" data-testid="design-walkthrough-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center shrink-0"><HelpCircle className="h-5 w-5 text-[#C9A84C]" /></span>
            <div>
              <p className="font-serif font-black text-[#1a2744] text-lg leading-tight">How to Replace a Campaign Design</p>
              <p className="text-xs text-slate-500 mt-0.5">A quick guided sequence for uploading, approving, and activating a replacement flyer or design. Admin-only.</p>
            </div>
          </div>
          <button onClick={onDismiss} className="p-1.5 rounded-lg text-slate-400 hover:text-[#1a2744] hover:bg-slate-100 shrink-0" title="Dismiss" data-testid="design-walkthrough-dismiss-x"><X className="h-4 w-4" /></button>
        </div>
        <ol className="mt-3 space-y-1.5">
          {WALKTHROUGH_STEPS.map((s, i) => (
            <li key={s.slice(0, 40)} className="flex gap-2.5 text-sm text-slate-700" data-testid={`design-walkthrough-step-${i + 1}`}>
              <span className="h-5 w-5 rounded-full bg-[#1a2744] text-[#C9A84C] text-[11px] font-black flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={scrollToUpload} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="design-walkthrough-upload-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Upload New Version
          </Button>
          <Button onClick={() => setRulesOpen(true)} variant="outline" className="h-10 font-bold btn-press" data-testid="design-walkthrough-rules-btn">
            <ShieldCheck className="h-4 w-4 mr-1.5" /> View Safety Rules
          </Button>
          <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="design-walkthrough-test-btn">
            <a href={`${API}/flyer/download/pdf?loc=other`} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4 mr-1.5" /> Test Public Download</a>
          </Button>
          <Button onClick={onDismiss} variant="outline" className="h-10 font-bold btn-press text-slate-500" data-testid="design-walkthrough-dismiss-btn">
            Dismiss
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Test Public Download counts one download — counters can be reset in Launch Content Setup → Download Analytics.</p>
        <SafetyRulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} confirmationText={confirmationText} />
      </CardContent>
    </Card>
  );
};

// ---- Part 2: Design Change Log (accountability only — never public) ----
const ACTION_BADGES = {
  uploaded: 'bg-slate-100 text-slate-600 border-slate-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  activated: 'bg-[#C9A84C]/15 text-[#7A5C13] border-[#C9A84C]',
  restored: 'bg-[#C9A84C]/15 text-[#7A5C13] border-[#C9A84C]',
  retired: 'bg-amber-50 text-amber-700 border-amber-200',
  deleted: 'bg-red-50 text-red-700 border-red-200',
  'made-public': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'made-private': 'bg-slate-100 text-slate-600 border-slate-200',
};

const ChangeLogTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/designs/change-log').then((r) => setData(r.data)).catch(() => toast.error('Failed to load change log'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveNote = async (entry, notes) => {
    try {
      await api.put(`/admin/designs/change-log/${entry.id}`, { notes });
      toast.success('Note saved');
      load();
    } catch { toast.error('Note save failed'); }
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const actions = data.actions || {};
  return (
    <div data-testid="design-change-log">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-600 mb-4 flex items-start gap-2" data-testid="change-log-note">
        <ScrollText className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{data.note}</span>
      </div>
      {data.items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="change-log-empty">No design changes recorded yet. Uploads, approvals, activations, retirements, restores, and deletions will appear here automatically.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((e) => (
            <Card key={e.id} data-testid="change-log-entry">
              <CardContent className="p-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <VBadge cls={ACTION_BADGES[e.action] || 'bg-slate-100 text-slate-600 border-slate-200'} testId={`change-log-action-${e.action}`}>{actions[e.action] || e.action}</VBadge>
                  <p className="text-sm font-bold text-[#1a2744] flex-1 min-w-0">{e.design_label}</p>
                  <p className="text-xs text-slate-400 shrink-0">{fmtDate(e.at)}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1 break-all">File: <strong className="text-slate-700">{e.file_name || '—'}</strong> · by {e.admin}</p>
                <div className="flex gap-1.5 mt-2">
                  <Input defaultValue={e.notes || ''} placeholder="Add a note…" onBlur={(ev) => { if (ev.target.value !== (e.notes || '')) saveNote(e, ev.target.value); }} className="h-9 text-xs" data-testid={`change-log-notes-${e.id}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Part 3: Print Proof Sheet ----
const ProofSheetTab = () => {
  const [last, setLast] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/designs/proof-sheet').then((r) => setLast(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/designs/proof-sheet');
      setResult(r.data);
      setLast(r.data);
      toast.success('Proof sheet generated — download the PDF below');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBusy(false);
  };

  const sheet = result || (last?.pdf ? last : null);
  return (
    <div data-testid="design-proof-sheet">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-600 mb-4 flex items-start gap-2" data-testid="proof-sheet-note">
        <FileCheck className="h-4 w-4 mt-0.5 shrink-0" />
        <span>One printable sheet of every <strong>active</strong> campaign design — flyers, yard signs, cards, QR and social graphics, and logos — with file, QR destination, public/approved status, and last updated date. Review everything here before ordering from a print shop. Nothing is published by generating it.</span>
      </div>
      <Button onClick={generate} disabled={busy} className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="proof-sheet-generate-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Printer className="h-4 w-4 mr-2" />} Generate Print Proof Sheet
      </Button>
      {sheet?.pdf && (
        <div className="mt-4" data-testid="proof-sheet-result">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="proof-sheet-download-pdf">
              <a href={fileUrl(sheet.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-4 w-4 mr-1.5" /> Download PDF</a>
            </Button>
            {sheet.png && (
              <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="proof-sheet-download-png">
                <a href={fileUrl(sheet.png)} target="_blank" rel="noopener noreferrer"><Image className="h-4 w-4 mr-1.5" /> PNG preview</a>
              </Button>
            )}
            {sheet.generated_at && <p className="text-xs text-slate-400">Generated {fmtDate(sheet.generated_at)}</p>}
          </div>
          {sheet.png && (
            <a href={fileUrl(sheet.pdf)} target="_blank" rel="noopener noreferrer" className="block mt-3 max-w-md">
              <img src={`${fileUrl(sheet.png)}?t=${encodeURIComponent(sheet.generated_at || '')}`} alt="Proof sheet preview (page 1)" className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="proof-sheet-preview-img" />
            </a>
          )}
          {result?.items && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="proof-sheet-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-bold">Design</th>
                    <th className="py-2 pr-3 font-bold">Active file / version</th>
                    <th className="py-2 pr-3 font-bold">Status</th>
                    <th className="py-2 font-bold">QR destination</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((it) => (
                    <tr key={it.key} className="border-b border-slate-100" data-testid={`proof-sheet-row-${it.key}`}>
                      <td className="py-2 pr-3 font-semibold text-[#1a2744]">{it.label}</td>
                      <td className="py-2 pr-3 text-slate-600 text-xs">{it.file_label}</td>
                      <td className="py-2 pr-3 text-slate-600 text-xs">{it.status} · {it.approved} · {it.updated}</td>
                      <td className="py-2 text-slate-600 text-xs">{it.qr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Activation dialog: safety scan + required confirmation checkbox ----
const ActivateDialog = ({ version, design, onClose, onDone }) => {
  const [scan, setScan] = useState(null);
  const [makePublic, setMakePublic] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setScan(null); setConfirmed(false); setMakePublic(true);
    if (version) {
      api.get(`/admin/designs/versions/${version.id}/safety`)
        .then((r) => setScan(r.data))
        .catch(() => toast.error('Safety check failed to load'));
    }
  }, [version]);

  if (!version) return null;
  const blocked = makePublic && scan && scan.blockers.length > 0;
  const canActivate = scan && !busy && (!makePublic || (confirmed && !blocked));

  const activate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/designs/versions/${version.id}/activate`, { public: makePublic, confirmed });
      toast.success(makePublic
        ? 'Version activated — it now serves as the public design for this asset.'
        : 'Version activated as Private — it is selected but not shown publicly.');
      (r.data.warnings || []).slice(0, 2).forEach((w) => toast.warning(w));
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="design-activate-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">Activate version — {design?.label}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">{version.original_name} · {fmtSize(version.size)} · uploaded {fmtDate(version.created_at)}</p>

        {!scan ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Running safety checks…</div>
        ) : (
          <div className="space-y-3">
            {scan.blockers.length === 0 && scan.warnings.length === 0 && (
              <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="design-safety-pass"><CheckCircle2 className="h-4 w-4" /> Automated checks passed.</p>
            )}
            {scan.blockers.map((b, i) => (
              <div key={`${i}-${String(b).slice(0, 30)}`} className="flex gap-2 rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-800" data-testid="design-safety-blocker">
                <XCircle className="h-4 w-4 shrink-0 mt-0.5" /> <span><strong>Blocker:</strong> {b}</span>
              </div>
            ))}
            {scan.warnings.map((w, i) => (
              <div key={`${i}-${String(w).slice(0, 30)}`} className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="design-safety-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{w}</span>
              </div>
            ))}
            <p className="text-[11px] text-slate-400">{scan.note}</p>

            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
              <div>
                <p className="text-sm font-bold text-[#1a2744]">Make Public</p>
                <p className="text-xs text-slate-500 mt-0.5">Serve this version on public downloads / public pages. Off = selected but Private.</p>
              </div>
              <Switch checked={makePublic} onCheckedChange={setMakePublic} data-testid="design-activate-public-switch" />
            </label>

            {makePublic && (
              <label className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer ${confirmed ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'}`}>
                <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" data-testid="design-activate-confirm-checkbox" />
                <span className="text-xs font-semibold text-[#1a2744] leading-relaxed">{scan.confirmation_text}</span>
              </label>
            )}

            <div className="flex gap-2 pt-1">
              <Button onClick={activate} disabled={!canActivate} className="flex-1 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="design-activate-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
                {makePublic ? 'Activate as Public design' : 'Activate (keep Private)'}
              </Button>
              <Button variant="outline" onClick={onClose} className="h-11 font-bold btn-press" data-testid="design-activate-cancel">Cancel</Button>
            </div>
            {blocked && <p className="text-xs text-red-700 font-semibold" data-testid="design-activate-blocked-note">Public activation is blocked until the issues above are fixed. You can still keep this version stored privately.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---- One design card ----
const DesignCard = ({ design, reload, onActivate }) => {
  const [cfg, setCfg] = useState(design.settings || {});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [openFields, setOpenFields] = useState(false);
  const [openVersions, setOpenVersions] = useState(design.versions.length > 0);
  const fileInput = useRef(null);

  useEffect(() => { setCfg(design.settings || {}); setOpenVersions(design.versions.length > 0); }, [design]);

  const saveSettings = async () => {
    setBusy('settings');
    try {
      await api.put(`/admin/designs/${design.key}/settings`, cfg);
      toast.success('Design fields saved');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const doUpload = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('notes', notes);
      await api.post(`/admin/designs/${design.key}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded — saved as Private / Not Public. Activate it when Steve approves.');
      setNotes('');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setBusy('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const deactivate = async (v) => {
    if (!window.confirm('Deactivate this version? Public downloads/pages fall back to the generated design.')) return;
    try {
      await api.post(`/admin/designs/versions/${v.id}/deactivate`);
      toast.success('Deactivated — generated design serves publicly again.');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const makePrivate = async (v) => {
    try {
      await api.put(`/admin/designs/versions/${v.id}`, { public: false });
      toast.success('Version set to Private');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveNotes = async (v, value) => {
    try {
      await api.put(`/admin/designs/versions/${v.id}`, { notes: value });
      toast.success('Notes saved');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeVersion = async (v) => {
    if (!window.confirm('Delete this inactive version and its file? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/designs/versions/${v.id}`);
      toast.success('Version deleted');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const sourceBadge = design.public_source === 'uploaded'
    ? <VBadge cls="border-emerald-200 bg-emerald-50 text-emerald-700" icon={Globe} testId={`design-source-${design.key}`}>Public: Uploaded version</VBadge>
    : design.public_source === 'generated'
      ? <VBadge cls="border-slate-200 bg-slate-100 text-slate-600" icon={Printer} testId={`design-source-${design.key}`}>Public: Generated design</VBadge>
      : <VBadge cls="border-slate-200 bg-slate-50 text-slate-500" icon={Lock} testId={`design-source-${design.key}`}>No public file yet</VBadge>;

  return (
    <Card data-testid={`design-card-${design.key}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-serif font-bold text-[#1a2744] leading-snug">{design.label}</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{design.description}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {sourceBadge}
            {cfg.visible === false && <VBadge cls="border-slate-200 bg-slate-100 text-slate-500" testId={`design-hidden-${design.key}`}>Hidden</VBadge>}
          </div>
        </div>

        {/* Generated files (kept intact) */}
        {design.generated_files.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`design-generated-${design.key}`}>
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mr-1">Generated:</span>
            {design.generated_files.map((g) => (
              <a key={g.filename} href={fileUrl(g.url)} target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border text-xs font-bold ${g.exists ? 'border-slate-200 text-[#1a2744] hover:bg-slate-50' : 'border-red-200 text-red-500 pointer-events-none'}`}
                data-testid={`design-generated-file-${design.key}-${g.filename}`}>
                <FileText className="h-3 w-3" /> {g.ext.toUpperCase()} {g.exists ? `· ${fmtSize(g.size)}` : '· missing'}
              </a>
            ))}
          </div>
        )}

        {design.edit_link && (
          <Button asChild variant="outline" size="sm" className="h-9 mt-3 font-bold" data-testid={`design-edit-link-${design.key}`}>
            <Link to={design.edit_link}><ExternalLink className="h-3.5 w-3.5 mr-1" /> {design.edit_label || 'Open editor'}</Link>
          </Button>
        )}

        {/* Editable design fields */}
        <Collapsible open={openFields} onOpenChange={setOpenFields} className="mt-3">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-bold text-[#1a2744] underline underline-offset-4" data-testid={`design-fields-toggle-${design.key}`}>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${openFields ? 'rotate-180' : ''}`} /> Design fields (title, slogans, URLs, QR, placement)
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {SETTINGS_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label className="block text-[11px] font-bold text-slate-500 mb-1">{label}</label>
                  <Input value={cfg[key] || ''} onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))} className="h-10" data-testid={`design-field-${design.key}-${key}`} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold text-slate-500 mb-1">Body / footer / disclaimer text</label>
                <Textarea value={cfg.body || ''} onChange={(e) => setCfg((c) => ({ ...c, body: e.target.value }))} rows={3} className="text-sm" data-testid={`design-field-${design.key}-body`} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                <Switch checked={cfg.visible !== false} onCheckedChange={(v) => setCfg((c) => ({ ...c, visible: v }))} data-testid={`design-visible-${design.key}`} /> Show on public site
              </label>
              <Button onClick={saveSettings} disabled={busy === 'settings'} size="sm" className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`design-fields-save-${design.key}`}>
                {busy === 'settings' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save fields
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Upload new version */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Version notes (optional) — e.g. New print shop artwork v2" className="h-11 flex-1 bg-white" data-testid={`design-upload-notes-${design.key}`} />
            <input ref={fileInput} type="file" accept={design.required_ext || '.pdf,.png,.jpg,.jpeg,.webp,.svg,.docx'} className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`design-upload-input-${design.key}`} />
            <Button onClick={() => fileInput.current?.click()} disabled={busy === 'upload'} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid={`design-upload-btn-${design.key}`}>
              {busy === 'upload' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload version (saved Private)
            </Button>
          </div>
          {design.required_ext && <p className="text-[11px] text-slate-400 mt-1.5">This design requires a {design.required_ext.replace('.', '').toUpperCase()} file so the public download keeps working.</p>}
        </div>

        {/* Version history */}
        {design.versions.length > 0 && (
          <Collapsible open={openVersions} onOpenChange={setOpenVersions} className="mt-3">
            <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-bold text-[#1a2744] underline underline-offset-4" data-testid={`design-versions-toggle-${design.key}`}>
              <History className="h-3.5 w-3.5" /> Version history ({design.versions.length})
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${openVersions ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-2 mt-2.5">
                {design.versions.map((v) => (
                  <div key={v.id} className={`rounded-xl border p-3 ${v.active ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-slate-200'}`} data-testid={`design-version-${v.id}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {v.active
                        ? <VBadge cls="border-[#C9A84C] bg-[#C9A84C]/15 text-[#7A5C13]" icon={CheckCircle2} testId="design-version-active-badge">Active version</VBadge>
                        : <VBadge cls="border-slate-200 bg-slate-100 text-slate-500" testId="design-version-inactive-badge">Inactive</VBadge>}
                      {v.public
                        ? <VBadge cls="border-emerald-200 bg-emerald-50 text-emerald-700" icon={Globe} testId="design-version-public-badge">Public</VBadge>
                        : <VBadge cls="border-slate-200 bg-slate-100 text-slate-600" icon={Lock} testId="design-version-private-badge">Private</VBadge>}
                      {v.approved && <VBadge cls="border-emerald-200 bg-emerald-50 text-emerald-700" icon={ShieldCheck} testId="design-version-approved-badge">Approved</VBadge>}
                    </div>
                    <p className="text-xs text-slate-600 mt-1.5 break-all">
                      <strong className="text-[#1a2744]">{v.original_name || v.filename}</strong>
                      {' '}· {String(v.ext || '').replace('.', '').toUpperCase()} · {fmtSize(v.size)} · {fmtDate(v.created_at)} · by {v.uploaded_by}
                    </p>
                    <div className="flex gap-1.5 mt-2">
                      <Input defaultValue={v.notes || ''} placeholder="Notes…" onBlur={(e) => { if (e.target.value !== (v.notes || '')) saveNotes(v, e.target.value); }} className="h-9 text-xs flex-1" data-testid={`design-version-notes-${v.id}`} />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold" data-testid={`design-version-preview-${v.id}`}>
                        <a href={fileUrl(v.file_url)} target="_blank" rel="noopener noreferrer"><Eye className="h-3 w-3 mr-1" /> Preview / Download</a>
                      </Button>
                      {!v.active && (
                        <Button onClick={() => onActivate(v, design)} size="sm" className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`design-version-activate-${v.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> {v.approved ? 'Restore / Activate' : 'Activate…'}
                        </Button>
                      )}
                      {v.active && !v.public && (
                        <Button onClick={() => onActivate(v, design)} size="sm" variant="outline" className="h-9 text-xs font-bold border-emerald-300 text-emerald-700" data-testid={`design-version-makepublic-${v.id}`}>
                          <Globe className="h-3 w-3 mr-1" /> Make Public…
                        </Button>
                      )}
                      {v.active && v.public && (
                        <Button onClick={() => makePrivate(v)} size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid={`design-version-makeprivate-${v.id}`}>
                          <Lock className="h-3 w-3 mr-1" /> Make Private
                        </Button>
                      )}
                      {v.active && (
                        <Button onClick={() => deactivate(v)} size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid={`design-version-deactivate-${v.id}`}>
                          <RefreshCw className="h-3 w-3 mr-1" /> Deactivate (use generated)
                        </Button>
                      )}
                      {!v.active && (
                        <Button onClick={() => removeVersion(v)} size="sm" variant="outline" className="h-9 text-xs font-bold text-red-600 border-red-200 hover:bg-red-50" data-testid={`design-version-delete-${v.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminDesigns() {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState('all');
  const [activating, setActivating] = useState(null); // {version, design}
  const [walkthroughDismissed, setWalkthroughDismissed] = useState(() => localStorage.getItem(WALKTHROUGH_KEY) === '1');

  const load = useCallback(() => {
    api.get('/admin/designs').then((r) => setData(r.data)).catch(() => toast.error('Failed to load designs'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismissWalkthrough = () => {
    localStorage.setItem(WALKTHROUGH_KEY, '1');
    setWalkthroughDismissed(true);
    toast.info('Walkthrough hidden — reopen it any time with the Help button.');
  };
  const showWalkthrough = () => {
    localStorage.removeItem(WALKTHROUGH_KEY);
    setWalkthroughDismissed(false);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const groups = data.groups || {};
  const designs = (data.designs || []).filter((d) => group === 'all' || d.group === group);

  return (
    <div data-testid="admin-designs-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Design Manager</h1>
        <div className="flex flex-wrap gap-2">
          {walkthroughDismissed && (
            <Button onClick={showWalkthrough} variant="outline" size="sm" className="font-bold" data-testid="designs-help-btn"><HelpCircle className="h-4 w-4 mr-1" /> Help</Button>
          )}
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/launch-content" data-testid="designs-link-flyer-kit"><Printer className="h-4 w-4 mr-1" /> Print Flyer Kit</Link></Button>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/story-media" data-testid="designs-link-story-media"><Camera className="h-4 w-4 mr-1" /> Story Media</Link></Button>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/hero" data-testid="designs-link-hero"><Image className="h-4 w-4 mr-1" /> Hero Manager</Link></Button>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/social" data-testid="designs-link-social"><Share2 className="h-4 w-4 mr-1" /> Share Preview</Link></Button>
        </div>
      </div>
      <p className="text-slate-500 text-sm mb-4">Every campaign design asset in one place — flyers, yard signs, cards, QR and social graphics, heroes, logos, and downloadable files. Upload replacement versions, keep history, and choose which version serves publicly.</p>

      {!walkthroughDismissed && <WalkthroughCard onDismiss={dismissWalkthrough} confirmationText={data.confirmation_text} />}

      <Tabs defaultValue="designs" data-testid="designs-tabs">
        <TabsList className="mb-4 h-auto flex-wrap">
          <TabsTrigger value="designs" className="font-bold" data-testid="designs-tab-designs"><Palette className="h-4 w-4 mr-1.5" /> Designs</TabsTrigger>
          <TabsTrigger value="changelog" className="font-bold" data-testid="designs-tab-changelog"><ScrollText className="h-4 w-4 mr-1.5" /> Change Log</TabsTrigger>
          <TabsTrigger value="proofsheet" className="font-bold" data-testid="designs-tab-proofsheet"><FileCheck className="h-4 w-4 mr-1.5" /> Print Proof Sheet</TabsTrigger>
        </TabsList>

        <TabsContent value="designs">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 text-sm text-amber-800 mb-4 flex items-start gap-2" data-testid="designs-privacy-note">
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <span><strong>New uploads are Private / Not Public by default.</strong> A version only replaces the public design when it is Active + Public + Approved — which requires passing safety checks and ticking the confirmation checkbox. Generated print tools stay intact and serve as the fallback. Download counters, placement tracking, and QR analytics keep working either way.</span>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4" data-testid="designs-group-filters">
            {[['all', 'All'], ...Object.entries(groups)].map(([value, label]) => (
              <button key={value} onClick={() => setGroup(value)}
                className={`h-9 px-3 rounded-full text-xs font-bold shrink-0 border transition-colors duration-150 ${group === value ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:bg-slate-50'}`}
                data-testid={`designs-filter-${value}`}>
                {label} ({value === 'all' ? data.designs.length : data.designs.filter((d) => d.group === value).length})
              </button>
            ))}
          </div>

          {designs.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="designs-empty">No designs in this group.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {designs.map((d) => <DesignCard key={d.key} design={d} reload={load} onActivate={(version, design) => setActivating({ version, design })} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="changelog">
          <ChangeLogTab />
        </TabsContent>

        <TabsContent value="proofsheet">
          <ProofSheetTab />
        </TabsContent>
      </Tabs>

      {activating && (
        <ActivateDialog version={activating.version} design={activating.design}
          onClose={() => setActivating(null)} onDone={load} />
      )}
    </div>
  );
}
